import React, { useRef } from "react";
import { message } from "antd";
import {
  ModalForm,
  ProFormInstance,
  ProFormDatePicker,
  ProFormDateRangePicker,
  ProFormDependency,
  ProFormSelect,
  ProFormSwitch,
  ProFormTextArea,
  ProFormTimePicker,
} from "@ant-design/pro-components";
import dayjs, { Dayjs } from "dayjs";
import { TechnicianTimeOff, TimeOffPayload, TimeOffType } from "@/services/app/tenant/technician-availability";

interface TimeOffFormValues {
  type: TimeOffType;
  partial_day: boolean;
  date_range?: [Dayjs, Dayjs];
  date?: Dayjs;
  off_time?: [Dayjs, Dayjs];
  note?: string;
}

export interface TimeOffFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Own request (future dates only, sent for approval) vs a manager adding approved time off */
  isSelf: boolean;
  /** Row being edited; null / omitted = new */
  editing?: TechnicianTimeOff | null;
  /** Persist the payload; throw to keep the modal open with the error shown */
  onSave: (payload: TimeOffPayload) => Promise<unknown>;
}

const toInitialValues = (timeOff?: TechnicianTimeOff | null): TimeOffFormValues => {
  if (!timeOff) {
    return { type: "time_off", partial_day: false };
  }
  const isPartial = !!timeOff.time_from;
  return {
    type: timeOff.type,
    partial_day: isPartial,
    date_range: isPartial ? undefined : [dayjs(timeOff.date_from), dayjs(timeOff.date_to)],
    date: isPartial ? dayjs(timeOff.date_from) : undefined,
    off_time: isPartial
      ? [dayjs(timeOff.time_from!.slice(0, 5), "HH:mm"), dayjs(timeOff.time_to!.slice(0, 5), "HH:mm")]
      : undefined,
    note: timeOff.note ?? undefined,
  };
};

const toPayload = (values: TimeOffFormValues): TimeOffPayload =>
  values.partial_day
    ? {
        type: values.type,
        date_from: values.date!.format("YYYY-MM-DD"),
        date_to: values.date!.format("YYYY-MM-DD"),
        time_from: values.off_time![0].format("HH:mm"),
        time_to: values.off_time![1].format("HH:mm"),
        note: values.note ?? null,
      }
    : {
        type: values.type,
        date_from: values.date_range![0].format("YYYY-MM-DD"),
        date_to: values.date_range![1].format("YYYY-MM-DD"),
        time_from: null,
        time_to: null,
        note: values.note ?? null,
      };

/**
 * Add / edit / request time off. Shared by the availability panel (Users
 * drawer + My Availability) and the profile menu's Request Time Off.
 */
const TimeOffFormModal: React.FC<TimeOffFormModalProps> = ({ open, onOpenChange, isSelf, editing, onSave }) => {
  const formRef = useRef<ProFormInstance>(null);
  const disabledDate = isSelf ? (d: Dayjs) => d.isBefore(dayjs(), "day") : undefined;

  return (
    <ModalForm<TimeOffFormValues>
      title={editing ? "Edit Time Off" : isSelf ? "Request Time Off" : "Add Time Off"}
      width={520}
      open={open}
      onOpenChange={onOpenChange}
      initialValues={toInitialValues(editing)}
      formRef={formRef}
      modalProps={{ destroyOnHidden: true }}
      submitter={{ searchConfig: { submitText: editing ? "Save" : isSelf ? "Send Request" : "Add", resetText: "Cancel" } }}
      onFinish={async () => {
        // onFinish's values are already strings in each picker's display
        // format (fieldProps.format wins over dateFormatter) — read the raw
        // dayjs values instead
        const values: TimeOffFormValues = formRef.current?.getFieldsValue();
        try {
          await onSave(toPayload(values));
          return true;
        } catch (error: any) {
          message.error(error?.response?.data?.error || error?.message || "Failed to save time off");
          return false;
        }
      }}
    >
      <ProFormSelect
        name="type"
        label="Type"
        rules={[{ required: true }]}
        fieldProps={{ allowClear: false }}
        options={[
          { label: "Time Off", value: "time_off" },
          { label: "Vacation", value: "vacation" },
        ]}
      />
      <ProFormSwitch name="partial_day" label="Partial Day" />
      <ProFormDependency name={["partial_day"]}>
        {({ partial_day }) =>
          partial_day ? (
            <>
              <ProFormDatePicker
                name="date"
                label="Date"
                rules={[{ required: true, message: "Date is required" }]}
                fieldProps={{ format: "M/D/YYYY", disabledDate, style: { width: "100%" } }}
              />
              <ProFormTimePicker.RangePicker
                name="off_time"
                label="Off Between"
                rules={[{ required: true, message: "Time range is required" }]}
                fieldProps={{ format: "h:mm A", use12Hours: true, minuteStep: 15, style: { width: "100%" } }}
              />
            </>
          ) : (
            <ProFormDateRangePicker
              name="date_range"
              label="Dates"
              rules={[{ required: true, message: "Date range is required" }]}
              fieldProps={{ format: "M/D/YYYY", disabledDate, style: { width: "100%" } }}
            />
          )
        }
      </ProFormDependency>
      <ProFormTextArea
        name="note"
        label={isSelf ? "Reason / Note" : "Note"}
        fieldProps={{ rows: 2, maxLength: 500, showCount: true }}
      />
    </ModalForm>
  );
};

export default TimeOffFormModal;
